import { Component, Inject, Injector, OnInit } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { WorkflowDialogWidth, WorkflowType } from "@common/ADAPT.Common.Model/embed/workflow";
import { WorkflowStatusEnum } from "@common/ADAPT.Common.Model/organisation/workflow-status";
import { PersonFlag } from "@common/ADAPT.Common.Model/person/person-flag.enum";
import { ImplementationKitArticle } from "@common/implementation-kit/implementation-kit-article.enum";
import { PERSONAL_CONFIGURATION_PAGE } from "@common/page-route-providers";
import { IAdaptRoute } from "@common/route/page-route-builder";
import { UserService } from "@common/user/user.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { IAdaptMenuItem } from "@common/ux/menu/menu.component";
import { ResponsiveService } from "@common/ux/responsive/responsive.service";
import { FeaturesService } from "@org-common/lib/features/features.service";
import { PersonFlagService } from "@org-common/lib/person/person-flag.service";
import { buildLocalWorkflow } from "@org-common/lib/workflow/workflow.interface";
import { StartOnboardingSearchParam, WorkflowService } from "@org-common/lib/workflow/workflow.service";
import { WorkflowRunDialogComponent } from "@org-common/lib/workflow/workflow-run-dialog/workflow-run-dialog.component";
import { AiTour, EmbedShellTour } from "app/tours/embed-shell-tour";
import { onboardingWorkflow } from "app/workflows/onboarding/onboarding-workflow";
import { combineLatest, lastValueFrom, Observable } from "rxjs";
import { startWith, switchMap } from "rxjs/operators";

@Component({
    selector: "adapt-personal-dashboard-page",
    templateUrl: "./personal-dashboard-page.component.html",
    styleUrl: "./personal-dashboard-page.component.css",
    standalone: false,
})
export class PersonalDashboardPageComponent extends BaseRoutedComponent implements OnInit {
    public readonly FeatureName = FeatureName;
    public personalConfigLink$: Observable<string>;
    public person$ = this.userService.currentPerson$;

    public showTourPrompt = false;

    private interactiveTourMenuItem: IAdaptMenuItem = {
        text: "Run interactive tour",
        onClick: () => this.runTour(),
    };

    private aiTourMenuItem: IAdaptMenuItem = {
        text: "Discover Lumia Assistant",
        onClick: () => this.runAiTour(),
        visible: false,
    };

    public tourMenuItems: IAdaptMenuItem[] = [{
        text: "Tours",
        icon: "fal fa-location-arrow me-n1",
        items: [this.interactiveTourMenuItem, this.aiTourMenuItem, {
            text: "Watch tour video",
            onClick: () => this.showTourVideo(),
        }],
    }];

    constructor(
        injector: Injector,
        personFlagService: PersonFlagService,
        responsiveService: ResponsiveService,
        private userService: UserService,
        private workflowService: WorkflowService,
        private dialogService: AdaptCommonDialogService,
        @Inject(PERSONAL_CONFIGURATION_PAGE) personalConfigurationPageRoute: IAdaptRoute<any>,
        featuresService: FeaturesService,
    ) {
        super(injector);

        this.personalConfigLink$ = personalConfigurationPageRoute.getRoute();

        personFlagService.isFlagEnabled$(PersonFlag.ShellTourDismissed).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((flagSet) => this.showTourPrompt = !flagSet);

        combineLatest([
            responsiveService.isMobileSize$,
            featuresService.featureStatusUpdated$.pipe(startWith(undefined)),
        ]).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(([isMobileSize]) => {
            this.interactiveTourMenuItem.visible = !isMobileSize;
            this.aiTourMenuItem.visible = !isMobileSize && featuresService.isFeatureActive(FeatureName.PlatformAI);
        });
    }

    public async ngOnInit() {
        this.navigationEnd.pipe(
            startWith(undefined),
            switchMap(() => this.workflowService.continueWorkflowFromSearchParam()),
            this.takeUntilDestroyed(),
        ).subscribe(async () => {
            const workflowStatus = await lastValueFrom(this.workflowService.getLatestStatusForWorkflow(onboardingWorkflow));
            const runOnboarding = this.getSearchParameterBoolValue(StartOnboardingSearchParam);
            if (runOnboarding || (workflowStatus && (workflowStatus.status === WorkflowStatusEnum.Incomplete || workflowStatus.status === WorkflowStatusEnum.Current))) {
                this.runOnboardingWorkflow();
            }
        });

        this.notifyActivated();
    }

    private runOnboardingWorkflow() {
        this.workflowService.executeJourney(onboardingWorkflow).pipe(
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public runTour() {
        this.guidedTourService.run(EmbedShellTour);
    }

    public runAiTour() {
        this.guidedTourService.run(AiTour);
    }

    public showTourVideo() {
        this.dialogService.open(WorkflowRunDialogComponent, {
            workflow: buildLocalWorkflow({
                name: "Tour video",
                workflowId: "TourVideo",
                type: WorkflowType.Workflow,
                dialogWidth: WorkflowDialogWidth.SemiLarge,
                isStateless: true,
                steps: [{
                    name: "Tour video",
                    ordinal: 0,
                    articleSlug: ImplementationKitArticle.EmbedOnboardingTour,
                }],
            }),
        }).subscribe();
    }
}
